'use client';
import * as z from 'zod';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@shadcn/components/ui/form';
import { useForm } from 'react-hook-form';
import { Course } from '@/payload/payload-types';
import { useLocale, useTranslations } from 'next-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormInput } from '@shadcn/components/ui/input';
import { Checkbox } from '@shadcn/components/ui/checkbox';
import { Button } from '../Button';
import ChevronRight from '../../_icons/Chevron-right';
import { useState } from 'react';
import { AttentionBox } from '../attention-box';
import { logToPayload } from '../../_utilities/log-to-payload';

export type WaitingListFormParams = {
	courseId: Course['id'];
	// hasSubmittedForm: boolean;
	// isCourseInCart: boolean;
	// addCourseToCart: () => void;
	onFormSubmit?: () => void;
	//selectedCourseVersion: Courseversion | null;
};

export const WaitingListForm: React.FC<WaitingListFormParams> = ({ courseId, onFormSubmit }) => {
	const t = useTranslations('Course');
	const locale = useLocale();
	const [isSuccess, setIsSuccess] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const formSchema = z.object({
		course: z.string(),
		firstName: z.string().min(1, {
			message: t('first-name-required'),
		}),
		lastName: z.string().min(1, {
			message: t('last-name-required'),
		}),
		email: z.coerce
			.string()
			.min(1, {
				message: t('email-required'),
			})
			.email({
				message: t('invalid-email'),
			}),
		phone: z.string(),
		acceptTerms: z.literal(true, {
			errorMap: () => ({ message: t('terms-required') }),
		}),
	});
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		disabled: isSuccess || isLoading,
		defaultValues: {
			course: courseId,
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
		},
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		try {
			setIsLoading(true);
			const res = await fetch(
				`${process.env.NEXT_PUBLIC_SERVER_URL}/api/waiting-list?locale=${locale}`,
				{
					body: JSON.stringify(values),
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					method: 'POST',
				},
			);

			if (res.ok) {
				const { doc, errors } = await res.json();
				if (errors) throw new Error(errors[0].message);

				if (doc) {
					setIsSuccess(true);
					if (onFormSubmit) {
						onFormSubmit();
					}
				}
			} else {
				return res.json().then(json => {
					throw json;
				});
			}
		} catch (err) {
			logToPayload('WaitingListForm error ', { error: err });
		}

		setIsLoading(false);
	}
	return (
		<Form {...form}>
			<form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
				<FormField
					control={form.control}
					name="course"
					render={({ field }) => (
						<FormItem className="mb-1">
							<FormControl>
								<FormInput type="hidden" {...field} />
							</FormControl>
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="firstName"
					render={({ field }) => (
						<FormItem className="mb-1">
							<FormControl>
								<FormInput type="text" {...field} placeholder={t('first-name')} />
							</FormControl>
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="lastName"
					render={({ field }) => (
						<FormItem className="mb-1">
							<FormControl>
								<FormInput type="text" {...field} placeholder={t('last-name')} />
							</FormControl>
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem className="mb-1">
							<FormControl>
								<FormInput type="email" {...field} placeholder={t('email')} />
							</FormControl>
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="phone"
					render={({ field }) => (
						<FormItem className="mb-1">
							<FormControl>
								<FormInput type="phone" {...field} placeholder={t('phone')} />
							</FormControl>
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="acceptTerms"
					render={({ field }) => (
						<FormItem className="space-y-0">
							<div className="flex flex-row items-center">
								<FormControl>
									<Checkbox
										checked={field.value}
										disabled={isSuccess || isLoading}
										className="bg-white"
										onCheckedChange={field.onChange}
									/>
								</FormControl>
								<FormLabel className="text-xs font-normal text-white">
									{t('terms-of-making-inqueries')}
								</FormLabel>
							</div>
						</FormItem>
					)}
				/>
				{isSuccess ? (
					<AttentionBox
						text={t('waitinglist-success')}
						iconType="check"
						showIcon={true}
						className="bg-success"
					></AttentionBox>
				) : (
					<Button
						loading={isLoading}
						data-fullstory="contact-form-send-button"
						type="submit"
						intent="secondary"
					>
						{t('send')}
						<ChevronRight />
					</Button>
				)}
			</form>
		</Form>
	);
};
